import { useState, useEffect, useRef } from 'react';

// Parses values like "116K+", "6.9M", "Top 2%", "4.4×", "65.4%", "57%", "25"
// Returns null if no numeric part found (value renders as-is).
function parse(val) {
  const s = String(val || '').trim();
  const m = s.match(/^([^0-9]*)([0-9]+\.?[0-9]*)(.*)$/);
  if (!m) return null;
  return { pre: m[1], num: parseFloat(m[2]), suf: m[3], dec: m[2].includes('.') };
}

export function AnimatedNumber({ value, duration = 950, style, className }) {
  const parsed = parse(value);
  const [display, setDisplay] = useState(value);
  const ref = useRef(null);
  const ran = useRef(false);

  useEffect(() => {
    if (!parsed || ran.current) return;
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || ran.current) return;
        ran.current = true;
        obs.disconnect();

        const { pre, num, suf, dec } = parsed;
        const t0 = performance.now();

        const tick = (now) => {
          const t = Math.min((now - t0) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
          const cur = eased * num;
          setDisplay(`${pre}${dec ? cur.toFixed(1) : Math.round(cur)}${suf}`);
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <span ref={ref} style={style} className={className}>
      {display}
    </span>
  );
}
